import type { Cache, Variables } from '@urql/exchange-graphcache';

const cachedSessionFields = ({ cache, bookingId }: { cache: Cache; bookingId?: string }) =>
  cache
    .inspectFields('Query')
    .filter(
      field =>
        field.fieldName === 'focusGroupSessions' &&
        (bookingId === undefined || field.arguments?.booking_id === bookingId),
    );

const invalidateSummary = ({ cache, bookingId }: { cache: Cache; bookingId?: string }) => {
  cache
    .inspectFields('Query')
    .filter(
      field =>
        field.fieldName === 'focusGroupSessionSummary' &&
        (bookingId === undefined || field.arguments?.booking_id === bookingId),
    )
    .forEach(field => cache.invalidate('Query', field.fieldName, field.arguments ?? undefined));
};

/**
 * Marks every cached focus-group session list stale, so whichever is on screen re-reads and the rest
 * re-read when next shown. `withSummary` for a write that adds, removes or moves a session; a seat
 * change leaves the summary's counts and times as they were.
 *
 * Without `bookingId` it covers every study, for writes that name only a submission.
 */
export const invalidateFocusGroupSessions = ({
  cache,
  bookingId,
  withSummary = false,
}: {
  cache: Cache;
  bookingId?: string;
  withSummary?: boolean;
}) => {
  cachedSessionFields({ cache, bookingId }).forEach(field =>
    cache.invalidate('Query', field.fieldName, field.arguments ?? undefined),
  );
  if (withSummary) {
    invalidateSummary({ cache, bookingId });
  }
};

type PushedSession = { _id?: string | null; start?: number | null } | null;

const inRange = ({ start, args }: { start: number; args: Variables | null }) =>
  (typeof args?.start !== 'number' || start >= args.start) && (typeof args?.end !== 'number' || start < args.end);

/**
 * Brings the cached session lists in line with a push of the sessions that changed.
 *
 * graphcache merges each pushed session into the `BookingSession` a list already holds, so a seat
 * change needs nothing here. Which sessions a list holds is the one thing a push cannot reach: each
 * list is its own `Query` field. A list that should now hold a pushed session and does not, or holds
 * one whose start has moved out of its range, is re-read along with the summary; the rest are left
 * alone, so a seat change costs no request.
 */
export const reconcileFocusGroupSessionChanges = ({
  cache,
  bookingId,
  sessions,
}: {
  cache: Cache;
  bookingId: string;
  sessions: readonly PushedSession[];
}) => {
  const placed = sessions.flatMap(session => {
    const key = session?._id ? cache.keyOfEntity({ __typename: 'BookingSession', _id: session._id }) : null;
    return key && typeof session?.start === 'number' ? [{ key, start: session.start }] : [];
  });
  // A pushed session without a start cannot say which list it belongs to.
  const isComplete = placed.length === sessions.length;

  const stale = cachedSessionFields({ cache, bookingId }).filter(field => {
    if (!isComplete) {
      return true;
    }
    const cached = cache.resolve('Query', field.fieldName, field.arguments ?? undefined);
    if (!Array.isArray(cached)) {
      return true;
    }
    const held = new Set(cached.filter(key => typeof key === 'string'));

    return placed.some(session => inRange({ start: session.start, args: field.arguments }) !== held.has(session.key));
  });

  stale.forEach(field => cache.invalidate('Query', field.fieldName, field.arguments ?? undefined));
  if (stale.length > 0) {
    invalidateSummary({ cache, bookingId });
  }
};
